<template lang="pug">
    .play-box
        template(v-if='!isLoading')
            game-header(
                @soundStateChanged="headerKey = !headerKey",
                :key="headerKey",
                slug="CosmoTour",
                game-type="mathMagic",
                :is-homework="!!exerciseId",
            )
        .warning(v-if='orientationChangedMidGame && !gameFinished')
            svg#rotate(
                version='1.1'
                xmlns='http://www.w3.org/2000/svg'
                x='0px' y='0px' width='40px'
                height='40px' viewbox='0 0 612 612'
                style='enable-background:new 0 0 612 612;'
                xml:space='preserve'
            )
                no-phone-icon
            div(v-if="orientationChangedMidGame")
                | {{ $i18n.t('trainer.trainer_games_rotateScreenMessage') }}
        .planets-box(v-if="gameBlocksCondition" ref="planetsBox")
        .game-info__container(ref="info", v-if="gameBlocksCondition")
            .game-info__block
                game-info(
                    :score="{left:answers.good, right: answers.bad}"
                    :time="getTime"
                    :is-olympiad="isOlympiad"
                    :parameters="parameters",
                    :game-allows-tips="false"
                )
                .tip-button-container
                    cs-button.tip-button(
                        v-if="tipIsOn",
                        color="blue",
                        type="button",
                        pattern="primary-outline",
                        @click="showTip"
                    )
                        | {{ "Подсказка" }}
        img.rocket(
            v-if="gameBlocksCondition && displayedRocket"
            ref="rocket"
            :src="displayedRocket.path | apiFile"
        )
        template(v-else-if='!isLoading')
                .play-box__game
                    game-header(
                        v-show='!gameFinished && gameStarted'
                        @soundStateChanged="headerKey = !headerKey",
                        :key="headerKey",
                        :time='getTime'
                        :slug="type"
                        :gameType="parameters.courseType"
                        :is-homework="!!exerciseId"
                    )
                .play-box__end-container(v-if='gameFinished && !isOlympiad')
                    fireworks(
                        v-if="gameFinished && !isOlympiad && isPassedSuccessfully"
                        :is-mobile="isMobile"
                    )
                    .play-box__reaction-container
                        .play-box__answers
                          span {{ $i18n.t('progress.progress_table_attempts') }}
                          span {{ amountOfAnswers }}
                        result-image.play-box__image(:status="isPassedSuccessfully ?  'success' : 'failure'")
                        .play-box__answers
                          span {{ $i18n.t('progress.progress_table_rightPercent') }}
                          span {{ answers.good }}
                    result-buttons(
                          :exercise-id="exerciseId"
                          :games-type="'memory-development'"
                          :show-restart-button="!showRestartButton"
                          @restartGame="replayGame"
                      )
                        slot
                            cs-button(v-if="!exerciseId && showRestartButton", @click="replayGame").play-box__result-button(
                                    type="button",
                                    color="blue"
                                    pattern="primary-outline"
                                )
                                | {{ $i18n.t('trainer.trainer_gamePlayBox_restart') }}
        cs-loader(v-else-if='isLoading')
</template>

<script>
    import { cloneDeep, sample } from 'lodash';
    import cosmoTourGameApi from '../../../../api/games/cosmoTourGameApi';
    import game from '../../../../utils/trainer/game';
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import { mapState } from 'vuex';
    import apiFile from '../../../../filters/api.filter';
    import CsButton from '../../../../elements/CsButton';
    import GameHeader from '../../GameHeader';
    import GameStatsApi from '../../../../api/games/gameStat';

    export default {
        components: { CsButton, GameHeader },
        mixins: [game, playBoxGameMixin],
        data() {
            return {
                answers: {
                    good: 0,
                    bad: 0,
                },
                openedPlanets: [],
                screenDiagonal: 0,
                necessities: [],
                rockets: [],
                soundInstance: null,
                validRockets: [],
                displayedRocket: null,
                displayedPlanets: [],
                imagesAreFetched: false,
                positionsArray: [],
                animationInProgress: false,
                stepsTaken: 0,
                planetsBox: null,
                defaultHorizontalMargin: 0,
                defaultVerticalMargin: 0,
                currentDistraction: null,
                gameStat: {},
                initialParams: null,
                initialOrientationVertical: false,
                orientationChangedMidGame: false,
                windowHeight: window.innerHeight,
                windowWidth: window.innerWidth,
                planetsBoxBoundingClientRect: null,
            };
        },
        methods: {
            async fetchData() {
                try {
                    await this.getExercise();
                    await this.getExample();
                } catch (err) {
                    this.$toasted.error(this.$root.$t('errors.getting_level'));
                    this.$router.push({ name: 'dashboard' });
                }
            },
            replayGame() {
                this.displayedPlanets = [];
                this.orientationChangedMidGame = false;
                this.parameters = cloneDeep(this.initialParams);
                this.displayedRocket = null;
                this.gameFinished = false;
                this.openedPlanets = this.openedPlanets.sort(() => 0.5 - Math.random());
                this.restartGame();
            },
            beforeStart() {
                this.$set(this.parameters, 'initialAmountOfPlanets', this.parameters.amountOfPlanets);
                this.prepareForGame = false;
                this.registerSounds();
                this.answers.good = 0;
                this.answers.bad = 0;
                !this.timer && this.setTimer();
                this.$nextTick(() => this.startGame());
            },
            registerSounds() {
                this.$createjs.Sound.removeAllSounds();
                this.$createjs.Sound.removeAllEventListeners();
                !this.isMemoryDevelopmentCourse && this.$createjs.Sound.addEventListener('fileload', this.soundLoad);
                this.$createjs.Sound.alternateExtensions = ['mp3', 'ogg'];
                this.$createjs.Sound.registerSounds(this.soundObjects);
            },
            soundLoad() {
                this.loadedSound++;
                if (this.loadedSound === this.soundObjects.length) {
                    this.isLoading = false;
                }
            },
            async getExample() {
                this.isLoading = true;
                try {
                    const paramsForRequest = {
                        subtype: this.game.type.value,
                    };
                    const data = await cosmoTourGameApi.getGameItems(paramsForRequest);
                    this.rockets = data.rockets;
                    this.openedPlanets = data.planets;
                    this.necessities = data.necessities;
                    this.beforeStart();
                } catch (err) {
                    this.showErrorMessage(err);
                }
                this.isLoading = false;
            },
            refreshGameStatBeforeSend() {
                this.gameStat.attempts = this.answers.good + this.answers.bad;
                this.gameStat.answers = this.answers.good;
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.isCorrect = true;
                this.gameStat.parameters = cloneDeep(this.parameters);
            },
            calculatePercent() {
                let total = this.answers.good + this.answers.bad;
                if (total > 0) {
                    return parseInt((this.answers.good / total) * 100);
                }
                return 0;
            },
            startGame() {
                this.screenDiagonal = Math.sqrt(screen.width * screen.width + screen.height * screen.height);
                this.gameStarted = true;
                if (this.answerDuration) {
                    this.startAnswerTimer();
                }
                this.determineDisplayedPlanets();
            },
            showTip() {
                let planet = document.getElementById(`${this.displayedRocket.value}`);

                const neededPlanet = this.openedPlanets.find(planet => {
                    return planet.value === this.displayedRocket.value;
                });

                planet.src = apiFile(neededPlanet.path);
            },
            determineDisplayedPlanets() {
                this.displayedPlanets = cloneDeep(this.openedPlanets.slice(0, this.parameters.amountOfPlanets));
                this.setRocketsArray();
            },
            setRocketsArray() {
                this.validRockets = [];
                let arrayOfPlanetValues = this.displayedPlanets.map(planet => planet.value);
                arrayOfPlanetValues.forEach(val => {
                    this.validRockets.push(this.rockets.find(rocket => rocket.value === val));
                });
                this.validRockets.map(rocket => {
                    return (rocket.isChosen = false);
                });
                this.randomizeRocket();
            },
            randomizeRocket() {
                this.switchRocket();
                this.imagesAreFetched = true;
                this.spawnPlanets();
            },
            getRandomCoordinates(maxForX, maxForY) {
                let x = Math.round(Math.random() * 10) % maxForX;
                let y = Math.round(Math.random() * 10) % maxForY;

                return { x, y };
            },
            spawnPlanets() {
                this.$nextTick(() => {
                    this.planetsBox = this.$refs.planetsBox;
                    const planetsCount = 10;
                    if (this.initialOrientationVertical) {
                        this.defaultVerticalMargin = this.planetsBox.clientHeight / 7;
                        this.defaultHorizontalMargin = this.planetsBox.clientWidth / 6;
                    } else {
                        this.defaultVerticalMargin = this.planetsBox.clientHeight / 4;
                        this.defaultHorizontalMargin = this.planetsBox.clientWidth / 12;
                    }

                    for (let i = 0; i < planetsCount; i++) {
                        let coordinates = {
                            x: 0,
                            y: 0,
                        };

                        let coordinatesAreSet = false;
                        while (!coordinatesAreSet) {
                            if (this.initialOrientationVertical) {
                                coordinates = this.getRandomCoordinates(5, 4);
                            } else {
                                coordinates = this.getRandomCoordinates(10, 3);
                            }
                            const existPosition = this.positionsArray.find(position => {
                                return position.x === coordinates.x && position.y === coordinates.y;
                            });
                            if (!existPosition) {
                                this.positionsArray.push(coordinates);
                                coordinatesAreSet = true;
                            }
                        }
                    }

                    this.displayedPlanets.forEach((planet, index) => {
                        this.createPlanet(planet, index);
                    });
                });
            },
            findPos(obj) {
                let curLeft = 0;
                let curTop = 0;
                if (obj.offsetParent) {
                    do {
                        curLeft += obj.offsetLeft;
                        curTop += obj.offsetTop;
                    } while ((obj = obj.offsetParent));
                }
                return { x: curLeft, y: curTop };
            },
            createPlanet(planet, index) {
                if (this.planetsBoxBoundingClientRect === null) {
                    this.planetsBoxBoundingClientRect = this.planetsBox.getBoundingClientRect();
                }
                let planetImg = document.createElement('img');
                planetImg.setAttribute('id', `${planet.value}`);
                planetImg.style.zIndex = '1';
                planetImg.src = apiFile(planet.path);
                planetImg.className = 'planet';
                planetImg.addEventListener('click', () => {
                    this.animateRocket(event, planet);
                });
                if (this.positionsArray.length) {
                    planetImg.style.left =
                        this.planetsBoxBoundingClientRect.left / 6 +
                        this.defaultHorizontalMargin * this.positionsArray[index].x +
                        'px';
                    planetImg.style.top =
                        this.planetsBoxBoundingClientRect.top / 5 +
                        this.defaultVerticalMargin * this.positionsArray[index].y +
                        'px';
                }
                planetImg.style.width = planetImg.style.height = this.defaultHorizontalMargin + 'px';
                planetImg.style.position = 'absolute';
                this.planetsBox.appendChild(planetImg);
            },
            animateRocket(event, planet) {
                if (this.animationInProgress) {
                    return;
                }
                this.animationInProgress = true;
                const rocket = this.$refs.rocket;
                const rocketPos = this.findPos(rocket);
                const delta = { x: event.pageX - rocketPos.x, y: event.pageY - rocketPos.y };
                let deg = 90 - Math.atan(-delta.y / -delta.x) * (180 / Math.PI);
                if (-delta.x < 0) {
                    deg = 180 + deg;
                }
                const animationDuration = (Math.sqrt(delta.x ** 2 + delta.y ** 2) / this.screenDiagonal) * 3000;
                rocket.style.webkitTransform = `rotate(${-deg}deg)`;
                const animationPromise = rocket.animate([{ transform: `translate(${delta.x}px,${delta.y}px)` }], {
                    duration: animationDuration,
                });
                animationPromise.onfinish = () => {
                    rocket.style.transform = 'rotate(0)';
                    rocket.style.left = event.pageX + 'px';
                    rocket.style.bottom = event.pageY + 'px';
                    this.checkAnswer(planet, event);
                    this.resetRocket(rocket, animationDuration);
                };
            },
            resetRocket(rocket, animationDuration) {
                const animationPromise = rocket.animate([{ transform: `translateY(-1000px)` }], {
                    duration: animationDuration,
                });
                animationPromise.onfinish = () => {
                    rocket.style.left = '40%';
                    rocket.style.bottom = -rocket.clientHeight / 2 + 'px';
                    this.$nextTick(() => {
                        this.switchRocket();
                    });
                    this.animationInProgress = false;
                };
            },
            switchRocket() {
                this.validRockets.map(rocket => {
                    return rocket.isChosen && (rocket.isChosen = false);
                });
                if (this.displayedRocket) {
                    const oldRocket = this.validRockets.find(rocket => rocket.value === this.displayedRocket.value);
                    oldRocket && (oldRocket.isChosen = true);
                }
                const notChosenRockets = this.validRockets.filter(rocket => !rocket.isChosen);
                this.displayedRocket = sample(notChosenRockets);
            },
            addNewPlanet() {
                const addItem = () => {
                    this.$nextTick(() => {
                        this.parameters.amountOfPlanets++;
                        let newPlanet = cloneDeep(this.openedPlanets[this.parameters.amountOfPlanets - 1]);
                        this.displayedPlanets.push(newPlanet);
                        this.validRockets = [];
                        let arrayOfPlanetValues = this.displayedPlanets.map(planet => {
                            return planet.value;
                        });
                        arrayOfPlanetValues.forEach(val => {
                            this.validRockets.push(this.rockets.find(rocket => rocket.value === val));
                        });
                        this.createPlanet(newPlanet, this.parameters.amountOfPlanets - 1);
                    });
                };
                if (this.parameters.type.value !== 'colors' && this.parameters.amountOfPlanets < 10) {
                    addItem();
                }
                if (this.parameters.type.value === 'colors' && this.parameters.amountOfPlanets < 9) {
                    addItem();
                }
            },
            checkAnswer(planet, event) {
                if (this.displayedRocket.value === planet.value) {
                    this.answers.good++;
                    if (this.parameters.complication.selected) {
                        this.stepsTaken++;
                        if (this.stepsTaken === this.parameters.complication.steps) {
                            this.stepsTaken = 0;
                            this.addNewPlanet();
                        }
                    }
                    if (planet.path !== apiFile(this.necessities[0].path)) {
                        event.target.src = apiFile(this.necessities[1].path);
                        setTimeout(() => {
                            planet.path = apiFile(this.necessities[0].path);
                            event.target.src = planet.path;
                        }, 2000);
                    } else {
                        event.target.src = apiFile(this.necessities[1].path);
                        setTimeout(() => {
                            event.target.src = planet.path;
                        }, 2000);
                    }
                }
                if (this.displayedRocket.value !== planet.value) {
                    this.answers.bad++;
                    const previousPath = event.target.src;
                    event.target.src = apiFile(this.necessities[2].path);
                    setTimeout(() => {
                        event.target.src = previousPath;
                    }, 2000);
                }
            },
            randomInRange(min, max) {
                return Math.floor(Math.random() * (max - min) + 1 + min);
            },
            createStat() {
                let course = 'memory-development';
                let stat = {
                    parameters: this.parameters,
                    game: 'CosmoTour',
                    course: course,
                    userId: this.user.id,
                    attempts: 0,
                    gameExerciseId:
                        this.exercise && this.exercise.sourceType === 'olympiadLevelTrainerStage'
                            ? undefined
                            : this.exerciseId,
                };
                return GameStatsApi.create(stat);
            },
        },
        computed: {
            amountOfAnswers() {
                return this.answers.good + this.answers.bad;
            },
            isPassedSuccessfully() {
                return this.answers.good > this.answers.bad;
            },
            tipIsOn() {
                return this.parameters && this.parameters.tip;
            },
            showRestartButton() {
                return !this.parameters.fromLessonConductingTemplate;
            },
            gameBlocksCondition() {
                return !this.gameFinished && this.imagesAreFetched && !this.orientationChangedMidGame;
            },
            isScreenVertical() {
                return this.windowHeight > this.windowWidth;
            },
            soundObjects() {
                let sounds = [];
                sounds.push(...this.gamesConfig.common.sounds);
                return sounds;
            },
            ...mapState({
                game: state => state.trainer.game,
            }),
        },
        mounted() {
            !this.initialParams && (this.initialParams = cloneDeep(this.parameters));
            this.initialOrientationVertical = this.isScreenVertical;
        },
        watch: {
            time(val) {
                if (
                    this.parameters &&
                    this.parameters.distraction &&
                    val % 3 === 0 &&
                    val < this.parameters.gameDuration.value
                ) {
                    if (!this.currentDistraction) {
                        this.currentDistraction = document.createElement('img');
                        this.currentDistraction.style.opacity = '0.7';
                        this.currentDistraction.style.position = 'absolute';
                        this.currentDistraction.style.width = this.defaultVerticalMargin * 2 + 'px';
                        this.currentDistraction.style.height = this.defaultVerticalMargin * 2 + 'px';
                        this.currentDistraction.style.zIndex = '0';
                    }
                    this.currentDistraction.src = `/imgs/cosmotour-distractions/${Math.floor(Math.random() * 17)}.png`;
                    let planetsBoxWidth = this.planetsBox.offsetWidth;
                    let planetsBoxHeight = this.planetsBox.offsetHeight;
                    this.currentDistraction.style.left =
                        +this.randomInRange(this.planetsBoxBoundingClientRect.left, planetsBoxWidth) + 'px';
                    this.currentDistraction.style.top =
                        this.randomInRange(this.planetsBoxBoundingClientRect.left, planetsBoxHeight) + 'px';
                    this.planetsBox.appendChild(this.currentDistraction);
                }
                if (val === this.parameters.gameDuration.value) {
                    if (this.currentDistraction) {
                        this.currentDistraction.style.display = 'none';
                        this.currentDistraction = null;
                    }
                    this.soundInstance = this.$createjs.Sound.play('gameDone');
                    [...this.planetsBox.children].forEach(planet => {
                        planet.removeEventListener('click', planet);
                    });
                    Array.from(document.getElementsByClassName('planet')).forEach(planet => planet.remove());
                    this.validRockets = [];
                    this.displayedPlanets = [];
                    this.positionsArray = [];
                    this.initialOrientationVertical = false;
                    this.gameFinished = true;
                    this.stopTimer();
                    if (!this.gameStat) {
                        this.createStat();
                    }
                    this.refreshGameStatBeforeSend();
                    this.user.type === 'student' && this.sendStat();
                }
            },
            isScreenVertical() {
                if (this.orientationChangedMidGame) {
                    this.orientationChangedMidGame = false;
                    Array.from(document.getElementsByClassName('planet')).forEach(
                        planet => (planet.style.display = 'block')
                    );
                    if (!this.gameFinished) {
                        this.setTimer();
                    }
                    return;
                }
                Array.from(document.getElementsByClassName('planet')).forEach(
                    planet => (planet.style.display = 'none')
                );
                this.stopTimer();
                this.orientationChangedMidGame = true;
            },
        },
        beforeDestroy() {
            if (this.currentDistraction) {
                this.currentDistraction.style.display = 'none';
                this.currentDistraction = null;
            }
            this.displayedRocket = null;
            this.user.type === 'student' && !this.exerciseId && this.sendStat();
        },
    };
</script>

<style scoped lang="scss">
    .play-box {
        display: flex;
        flex: 1 1 auto;
        align-items: stretch;
        justify-content: center;
        width: 100%;
        position: relative;
    }

    .play-box__game {
        height: 100%;
        max-width: 85vw;
    }

    .rocket {
        width: calc(10vw + 15px);
        height: calc(10vw + 15px);
        transform: rotate(0);
        z-index: 2;
        left: 40%;
        bottom: calc((-10vw - 15px) / 2);
        position: absolute;
        align-self: center;
    }

    .game-info__container {
        margin-top: 1%;
        margin-inline-start: auto;
    }

    .planets-box {
        background-image: url('/imgs/games/cosmotour/background.jpg');
        background-size: contain;
        border-radius: 15px;
        width: 100%;
        margin-top: 1%;
        margin-inline-end: auto;
        transition-duration: 0.4s;
        min-height: 230px;
    }

    .game-info__block {
        top: 0;
        left: 0;
    }

    .play-box__end-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .play-box__reaction-container {
        display: flex;
        align-items: center;
        flex-direction: column-reverse;
        justify-content: center;
        width: 100%;
        @include media($md) {
            flex-direction: row;
        }
    }

    .play-box__answers {
        color: $hsl-color-green-58;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        margin: 20px 0;
        font-weight: 500;
        font-size: 20px;
        line-height: 21px;
        text-align: center;
        @include media($md) {
            margin: 0;
            font-size: 22px;
            line-height: 26px;
        }
        @include media($lg) {
            font-size: 32px;
            line-height: 38px;
        }
        @include media($xl) {
            font-size: 42px;
            line-height: 50px;
        }
    }

    .play-box__image {
        min-height: 450px;
        max-height: 500px;
        max-width: 100%;
        order: 3;
        margin-bottom: 30px;
        @include media($sm) {
            max-height: 600px;
        }
        @include media($md) {
            max-height: 650px;
            order: initial;
            margin-bottom: 40px;
        }
    }

    .tip-button-container {
        display: flex;
        justify-content: center;
    }

    .tip-button {
        margin: 5px;
        color: $x-color-white;
        background-color: $hsl-color-blue-56;
        align-self: center;
        font-size: 14px;
        padding: 10px 15px;
        @include media($sm) {
            font-size: 16px;
            padding: 15px 30px;
            width: 100%;
        }
    }

    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }
</style>
